import React, { useState, useEffect } from "react";
import { DropdownSelect } from "@vds/selects";
import { TIME_CONSTANTS, DEFAULT_TIME_RANGE } from "../../constants/dateTime.constants";
import "./CustomDropdownTimePicker.css";

function getTimeOptions() {
  const options = Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = i % 2 === 0 ? TIME_CONSTANTS.MINUTE_ZERO : TIME_CONSTANTS.MINUTE_THIRTY;
    const period = hour >= 12 ? TIME_CONSTANTS.PM : TIME_CONSTANTS.AM;
    const formattedHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${formattedHour}:${minute} ${period}`;
  });
  options.push(`11:59 ${TIME_CONSTANTS.PM}`);
  return options;
}

const timeOptions = getTimeOptions();
const timeValues = timeOptions.map((time) => {
  const [timePart, period] = time.split(" ");
  let [hour, minute] = timePart.split(":");
  hour = parseInt(hour);
  minute = parseInt(minute);
  if (period === TIME_CONSTANTS.PM && hour !== 12) hour += 12;
  if (period === TIME_CONSTANTS.AM && hour === 12) hour = 0;
  return hour * 60 + minute;
});

const CustomDropdownTimePicker = ({ from, to, onChange, style, errorCallback, fromDate, toDate }) => {
  const [startTime, setStartTime] = useState(from || DEFAULT_TIME_RANGE.START_TIME);
  const [endTime, setEndTime] = useState(to || DEFAULT_TIME_RANGE.END_TIME);
  const [error, setError] = useState("");

  useEffect(() => {
    if (onChange) onChange({ startTime, endTime });
  }, [startTime, endTime]);

  useEffect(() => {
    if (!fromDate || !toDate) return;

    const normalizedFromDate = new Date(fromDate).toDateString();
    const normalizedToDate = new Date(toDate).toDateString();
    const startTimeValue = timeValues[timeOptions.indexOf(startTime)];
    const endTimeValue = timeValues[timeOptions.indexOf(endTime)];

    let isValidTimeRange = false;

    if (normalizedFromDate === normalizedToDate) {
      isValidTimeRange = endTimeValue > startTimeValue && (endTimeValue - startTimeValue) <= 720;
    } else {
      const minutesFromStartToMidnight = (24 * 60) - startTimeValue;
      const totalMinutes = minutesFromStartToMidnight + endTimeValue;
      isValidTimeRange = totalMinutes <= 720;
    }

    if (!isValidTimeRange) {
      setStartTime(DEFAULT_TIME_RANGE.START_TIME);
      setEndTime(DEFAULT_TIME_RANGE.END_TIME);
    }
  }, [fromDate, toDate]);

  const startTimeValue = timeValues[timeOptions.indexOf(startTime)];
  const endTimeValue = timeValues[timeOptions.indexOf(endTime)];

  const isDisabled = !fromDate || !toDate;

  const getCurrentTimeInMinutes = () => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  };

  const getCeilingTime = (timeInMinutes) => {
    return Math.ceil(timeInMinutes / 30) * 30;
  };

  let startTimeOptions = timeOptions;
  let toTimeOptions = timeOptions;

  if (fromDate && toDate) {
    const normalizedFromDate = new Date(fromDate).toDateString();
    const normalizedToDate = new Date(toDate).toDateString();
    const isFromToday = normalizedFromDate === new Date().toDateString();
    const isToToday = normalizedToDate === new Date().toDateString();

    const ceilingTimeMinutes = (isFromToday || isToToday) ? getCeilingTime(getCurrentTimeInMinutes()) : Infinity;

    if (normalizedFromDate === normalizedToDate) {
      // Same day - apply ceiling constraint if today
      startTimeOptions = timeOptions.filter((_, idx) => {
        const timeValue = timeValues[idx];
        return timeValue <= ceilingTimeMinutes;
      });

      toTimeOptions = timeOptions.filter((_, idx) => {
        const timeValue = timeValues[idx];
        return timeValue > startTimeValue && (timeValue - startTimeValue) <= 720 && timeValue <= ceilingTimeMinutes;
      });
    } else if (normalizedFromDate !== normalizedToDate) {
      // Different days - one day apart
      // Filter start times if fromDate is today
      if (isFromToday) {
        startTimeOptions = timeOptions.filter((_, idx) => timeValues[idx] <= ceilingTimeMinutes);
      }

      // Filter end times using cross-midnight calculation
      const minutesFromStartToMidnight = (24 * 60) - startTimeValue;
      if (isToToday) {
        toTimeOptions = timeOptions.filter((_, idx) => {
          const timeValue = timeValues[idx];
          const totalMinutes = minutesFromStartToMidnight + timeValue;
          return totalMinutes <= 720 && timeValue <= ceilingTimeMinutes;
        });
      } else {
        toTimeOptions = timeOptions.filter((_, idx) => {
          const timeValue = timeValues[idx];
          const totalMinutes = minutesFromStartToMidnight + timeValue;
          return totalMinutes <= 720;
        });
      }
    }
  }

  useEffect(() => {
    if (!fromDate || !toDate) {
      setError("");
      if (errorCallback) errorCallback("");
      return;
    }

    const normalizedFromDate = new Date(fromDate).toDateString();
    const normalizedToDate = new Date(toDate).toDateString();
    const isOneDayApart = normalizedFromDate !== normalizedToDate;

    let totalMinutes;
    
    if (isOneDayApart) {
      const minutesFromStartToMidnight = (24 * 60) - startTimeValue;
      totalMinutes = minutesFromStartToMidnight + endTimeValue;
    } else {
      if (endTimeValue <= startTimeValue) {
        setError("End time must be after start time.");
        if (errorCallback) errorCallback("End time must be after start time.");
        return;
      }
      totalMinutes = endTimeValue - startTimeValue;
    }

    if (totalMinutes > 720) {
      setError("Max time range is 12 hours.");
      if (errorCallback) errorCallback("Max time range is 12 hours.");
    } else {
      setError("");
      if (errorCallback) errorCallback("");
    }
  }, [startTime, endTime, startTimeValue, endTimeValue, fromDate, toDate, errorCallback]);

  return (
    <div style={style}>
      <div className="time-picker-frame">
        <div className={`time-picker-dropdown-wrapper ${isDisabled ? 'disabled' : ''}`}>
          <div className="time-picker-overlay">
            <span className="time-picker-label">From</span>
            <span className="time-picker-value">{startTime}</span>
          </div>
          <DropdownSelect
            className="time-picker-dropdown"
            value={startTime}
            onChange={e => setStartTime(e.target.value)}
            disabled={isDisabled}
          >
            {startTimeOptions.map(time => (
              <option key={time} value={time}>{time}</option>
            ))}
          </DropdownSelect>
        </div>
        <div className={`time-picker-dropdown-wrapper ${isDisabled ? 'disabled' : ''}`}>
          <div className="time-picker-overlay">
            <span className="time-picker-label">To</span>
            <span className="time-picker-value">{endTime}</span>
          </div>
          <DropdownSelect
            className="time-picker-dropdown"
            value={endTime}
            onChange={e => setEndTime(e.target.value)}
            disabled={isDisabled}
            error={!!error}
            errorText={error}
          >
            <option key={"select time"} value={""}>{"select time"}</option>
            {toTimeOptions.map(time => (
              <option key={time} value={time}>{time}</option>
            ))}
          </DropdownSelect>
        </div>
      </div>
    </div>
  );
};

export default CustomDropdownTimePicker;
